import { useTranslation } from "react-i18next";
import { Address, Defect, DefectType, StatusIcon } from "../..";
import { Alert, Button, Card, Icon, Label } from "imagine-ui";
import { Api } from "imagine-essentials";
import { useParams, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { DateTimeFormat, DateTimeText, DateTimeTools } from "imagine-datetime";
import { UserRole } from "imagine-users";

interface Props {
  address: Address;
  errorMessage?: string;
  defectInfoUrl: string;
  createDefectUrl: string;
  showLink?: boolean;
  onApprove: () => void;
}

export const AddressInfoCards = (props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openDefect = (id: number) => {
    navigate(props.defectInfoUrl + id);
  };

  const defaultDefects = useMemo(() => {
    return props.address.defects?.filter(
      (defect) => defect.type === DefectType.DEFAULT
    );
  }, [props.address.defects]);

  const defaultResidentDefects = useMemo(() => {
    if (props.address.defects === undefined) return [];
    return props.address.defects?.filter(
      (defect) =>
        defect.type === DefectType.DEFAULT && defect.userRole === UserRole.NONE
    );
  }, [props.address.defects]);

  const deadlineExceeded = useMemo(() => {
    if (!props.address.deadline) return false;
    const today = DateTimeText.getDateTimeText(
      new Date(),
      DateTimeFormat.BACKEND_DATE
    );
    return !DateTimeTools.isDateBeforeOrEqual(today, props.address.deadline);
  }, [props.address.deadline]);

  const canCreateDefect = useMemo(() => {
    if (props.address.approved) return false;
    return !deadlineExceeded;
  }, [props.address.deadline, props.address.approved, deadlineExceeded]);

  return (
    <div>
      {props.errorMessage && (
        <Alert color="danger" className="mb">
          {props.errorMessage}
        </Alert>
      )}
      <div className="column gap">
        <h1 className="text-center">{props.address.name}</h1>
        <h5 className="text-center">{props.address.projectName}</h5>

        {props.address.deadline && (
          <div>
            <span className="text-bold">{t("punchlist:deadline")}:</span>
            <span className="ml-xs">
              {DateTimeText.getDbDateTimeText(
                props.address.deadline,
                DateTimeFormat.LONG_DATE
              )}
            </span>
          </div>
        )}

        <Label>{t("punchlist:defects")}</Label>
        <div className="column gap-sm">
          {defaultDefects &&
            defaultDefects.map((defect: Defect) => (
              <div
                key={defect.id}
                className="grid columns-2 columns-1--sm items-y-center clickable border shadow gap-sm px py-sm bg-secondary-hover"
                onClick={() => openDefect(defect.id)}
              >
                <div className="">{defect.title}</div>
                <div className="row row--sm items-right items-center--sm">
                  <StatusIcon status={defect.residentStatus} />
                  <StatusIcon
                    status={defect.contractorStatus}
                    className="mx-xs"
                  />
                  <StatusIcon status={defect.managerStatus} />
                </div>
              </div>
            ))}
        </div>
        <div className="row gap">
          <Button to={props.createDefectUrl} disabled={!canCreateDefect}>
            {t("punchlist:createDefect")}
          </Button>
          {defaultResidentDefects.length === 0 && (
            <Button onClick={props.onApprove} className="ml-sm" outline>
              {t("punchlist:approveWithoutDefects")}
            </Button>
          )}
          <div className="flex-1"></div>
          <div className="row items-y-center">
            {props.address.approved && (
              <span className="text-success">
                <Icon name="check" className="mr-xs" />
                {t("punchlist:approvedWithoutDefects")}
              </span>
            )}
            {!props.address.approved && deadlineExceeded && (
              <span className="text-danger">
                {t("punchlist:deadlineExceeded")}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
